export { default as RichTextHeading } from './RichTextHeading';
import React, { FC } from 'react';
import classNames from 'classnames';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import {
  Block,
  BLOCKS,
  Document,
  Inline,
  MARKS,
} from '@contentful/rich-text-types';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { isRichText } from '../RichText/RichText';

interface RichTextHeadingProps {
  classNames?: string;
  content: Document | Block | Inline;
  classes?: string;
  italicClass?: string;
  boldClass?: string;
  omitSizeString?: boolean;
}

const renderDocument = (
  content: any,
  classes?: string,
  italicClass?: string,
  boldClass?: string,
  omitSizeString?: boolean,
) => {
  if (!isRichText(content)) return null;

  const options: Options = {
    renderMark: {
      [MARKS.ITALIC]: (text) => (
        <span className={classNames('font-bradford font-normal', italicClass)}>
          {text}
        </span>
      ),
      [MARKS.BOLD]: (text) => (
        <span className={classNames('font-bold', boldClass)}>{text}</span>
      ),
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (_node, children) => (
        <Heading
          tag={HeadingTag.H1}
          size={omitSizeString ? '' : HeadingSize.H1}
          text={children}
          classes={classNames('mb-4 md:mb-6', classes)}
        />
      ),
      [BLOCKS.HEADING_2]: (_node, children) => (
        <Heading
          tag={HeadingTag.H2}
          size={omitSizeString ? '' : HeadingSize.H2}
          text={children}
          classes={classNames('mb-4 md:mb-6', classes)}
        />
      ),
      [BLOCKS.HEADING_3]: (_node, children) => (
        <Heading
          tag={HeadingTag.H3}
          size={omitSizeString ? '' : HeadingSize.H3}
          text={children}
          classes={classNames('mb-4 md:mb-6', classes)}
        />
      ),
      [BLOCKS.HEADING_4]: (_node, children) => (
        <Heading
          tag={HeadingTag.H4}
          size={omitSizeString ? '' : HeadingSize.H4}
          text={children}
          classes={classNames('mb-4 md:mb-6', classes)}
        />
      ),
    },
    // Allows for shift + enter breaks
    renderText: (text) => {
      return text
        .split('\n')
        .reduce<React.ReactNode[]>((children, textSegment, index) => {
          const elementsToAdd: React.ReactNode[] = [];
          if (index > 0) {
            elementsToAdd.push(<br key={index} />);
          }
          elementsToAdd.push(textSegment);
          return [...children, ...elementsToAdd];
        }, []);
    },
  };

  const renderRichText = documentToReactComponents(content, options);
  return renderRichText ? renderRichText : null;
};

const RichTextHeading: FC<RichTextHeadingProps> = ({
  content,
  classNames,
  classes,
  italicClass,
  boldClass,
  omitSizeString,
}) => (
  <div className={classNames} data-testid="rich-text-heading">
    {renderDocument(content, classes, italicClass, boldClass, omitSizeString)}
  </div>
);

export default React.memo(RichTextHeading);
